import React, { useState, useEffect } from 'react'
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Input, 
  FormControl, 
  FormLabel, 
  FormErrorMessage,
  useColorModeValue,
  Icon,
  IconButton
} from '@chakra-ui/react'
import { FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa'
import { useAuthContext } from '../../contexts/AuthContext'

interface LoginFormProps {
  onSuccess?: () => void
  onSwitchToRegister?: () => void
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onSwitchToRegister }) => {
  const { signIn, sendPasswordReset, error, loading, clearError } = useAuthContext()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [resetCooldown, setResetCooldown] = useState(0)
  
  const cardBg = useColorModeValue('#191919', '#191919')
  const borderColor = useColorModeValue('gray.700', 'gray.700')
  const textColor = useColorModeValue('white', 'white')
  const labelColor = useColorModeValue('gray.300', 'gray.300')
  const inputBg = useColorModeValue('#191919', '#191919')
  const inputBorder = useColorModeValue('gray.600', 'gray.600')
  const placeholderColor = useColorModeValue('gray.400', 'gray.400')

  // Password reset cooldown timer
  useEffect(() => {
    if (resetCooldown > 0) {
      const timer = setTimeout(() => {
        setResetCooldown(prev => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resetCooldown])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    
    try {
      await signIn(email, password)
      onSuccess?.()
    } catch (error) {
      // Error is handled by the auth hook
    }
  }

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    if (resetCooldown > 0) return
    clearError()
    
    // Set cooldown immediately to prevent multiple clicks
    setResetCooldown(10)
    
    try {
      await sendPasswordReset(email)
      setResetEmailSent(true)
    } catch (error) {
      // Error is handled by the auth hook
    }
  }

  if (showPasswordReset) {
    return (
      <Box maxW="md" mx="auto" bg={cardBg} rounded="lg" shadow="md" p={6} border="1px" borderColor={borderColor}>
        <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={6} color={textColor}>
          Reset Password
        </Text>
        
        {resetEmailSent ? (
          <VStack spacing={4} textAlign="center">
            <Box color="green.400">
              <Icon as={FaCheck} w={12} h={12} mx="auto" mb={2} />
              <Text fontSize="lg" fontWeight="semibold">Email Sent!</Text>
            </Box>
            <Text color="gray.300" mb={4}>
              We've sent a password reset link to <Text as="span" fontWeight="bold" color="white">{email}</Text>
            </Text>
            <Button
              w="full"
              bg="#3182ce"
              color="white"
              _hover={{ bg: '#2c5aa0' }}
              onClick={() => {
                setShowPasswordReset(false)
                setResetEmailSent(false)
              }}
            >
              Back to Login
            </Button>
          </VStack>
        ) : (
          <VStack as="form" onSubmit={handlePasswordReset} spacing={4}>
            <FormControl>
              <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
                Email Address
              </FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                bg={inputBg}
                borderColor={inputBorder}
                color="white"
                _placeholder={{ color: placeholderColor }}
                _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
                placeholder="Enter your email"
              />
            </FormControl>
            
            {error && (
              <Box color="red.400" fontSize="sm" bg="red.900" opacity={0.2} p={3} rounded="md" border="1px" borderColor="red.800">
                {error}
              </Box>
            )}
            
            <Button
              type="submit"
              w="full"
              bg="#3182ce"
              color="white"
              _hover={{ bg: resetCooldown > 0 ? '#3182ce' : '#2c5aa0' }}
              isDisabled={loading || resetCooldown > 0}
              opacity={loading || resetCooldown > 0 ? 0.5 : 1}
            >
              {loading ? 'Sending...' : resetCooldown > 0 ? `Send Reset Link (${resetCooldown}s)` : 'Send Reset Link'}
            </Button>
            
            <Button
              variant="ghost"
              w="full"
              color="gray.400"
              _hover={{ bg: 'gray.800' }}
              onClick={() => setShowPasswordReset(false)}
            >
              Back to Login
            </Button>
          </VStack>
        )}
      </Box>
    )
  }

  return (
    <Box maxW="md" mx="auto" bg={cardBg} rounded="lg" shadow="md" p={6} border="1px" borderColor={borderColor}>
      <Text fontSize="2xl" fontWeight="bold" textAlign="center" mb={6} color={textColor}>
        Log In
      </Text>
      
      <VStack as="form" onSubmit={handleSubmit} spacing={4}>
        <FormControl>
          <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
            Email Address
          </FormLabel>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            bg={inputBg}
            borderColor={inputBorder}
            color="white"
            _placeholder={{ color: placeholderColor }}
            _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
            placeholder="Enter your email"
          />
        </FormControl>
        
        <FormControl>
          <FormLabel color={labelColor} fontSize="sm" fontWeight="medium">
            Password
          </FormLabel>
          <Box position="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              bg={inputBg}
              borderColor={inputBorder}
              color="white"
              _placeholder={{ color: placeholderColor }}
              _focus={{ borderColor: '#3182ce', boxShadow: '0 0 0 1px #3182ce' }}
              placeholder="Enter your password"
              pr="3rem"
            />
            <IconButton
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              icon={<Icon as={showPassword ? FaEyeSlash : FaEye} />}
              size="sm"
              position="absolute"
              right={2}
              top="50%"
              transform="translateY(-50%)"
              variant="ghost"
              color="gray.400"
              _hover={{ bg: 'transparent' }}
              onClick={() => setShowPassword(!showPassword)}
            />
          </Box>
        </FormControl>
        
        {error && (
          <Box color="red.400" fontSize="sm" bg="red.900" opacity={0.2} p={3} rounded="md" border="1px" borderColor="red.800">
            {error}
          </Box>
        )}
        
        <Button
          type="submit"
          w="full"
          bg="#3182ce"
          color="white"
          _hover={{ bg: '#2c5aa0' }}
          isDisabled={loading}
          opacity={loading ? 0.5 : 1}
        >
          {loading ? 'Logging In...' : 'Log In'}
        </Button>
        
        <VStack spacing={2} textAlign="center">
          <Button
            variant="link"
            color="blue.400"
            _hover={{ color: 'blue.300' }}
            fontSize="sm"
            onClick={() => setShowPasswordReset(true)}
          >
            Forgot your password?
          </Button>
          
          {onSwitchToRegister && (
            <Text color="gray.400" fontSize="sm">
              Don't have an account?{' '}
              <Button
                variant="link"
                color="blue.400"
                _hover={{ color: 'blue.300' }}
                fontWeight="medium"
                onClick={onSwitchToRegister}
              >
                Sign up
              </Button>
            </Text>
          )}
        </VStack>
      </VStack>
    </Box>
  )
} 